import Vue from 'vue'
import VueI18n from 'vue-i18n'
import {i18nApi} from '@core/api/index'
import {
    APPLICATION_TRANSLATION_GROUP,
    CORE_TRANSLATION_GROUP,
    DOCUMENT_REVIEW_TRANSLATION_GROUP,
    PRODUCT_COMPLIANCE_TRANSLATION_GROUP,
    RECEIVED_REQUEST_REPLY_TRANSLATION_GROUP,
    SUPPLIER_SELECTION_TRANSLATION_GROUP,
} from './groups'

Vue.use(VueI18n)

const supported = ['en', 'sv']
const loadedTranslationGroups = {}
const translationGroupPromises = {}
const activeTranslationGroups = new Set([CORE_TRANSLATION_GROUP])

export const translationLoadingConfig = {
    loadFallbackTranslations: false,
}

const normalizeGroups = groups => Array.from(new Set(
    (Array.isArray(groups) ? groups : [groups]).filter(Boolean),
))

const bootstrapGroups = groups => {
    const normalizedGroups = normalizeGroups(groups)

    return normalizedGroups.length > 0 ? normalizedGroups : [CORE_TRANSLATION_GROUP]
}

const groupsForLanguage = language => {
    if (!loadedTranslationGroups[language]) {
        loadedTranslationGroups[language] = new Set()
    }

    return loadedTranslationGroups[language]
}

const markGroupsLoaded = (language, groups) => {
    const loaded = groupsForLanguage(language)
    normalizeGroups(groups).forEach(group => loaded.add(group))
}

const translationStorageKey = (language, group) => `translations.${language}.${group}`

let locale = localStorage.getItem('locale') || navigator.language.substring(0, 2)
if (!supported.includes(locale)) {
    locale = 'en'
}

const loadLocaleMessages = () => {
    if (document.hasOwnProperty('hamlet') && document.hamlet.translations) {
        const groups = bootstrapGroups(document.hamlet.translationGroups)
        markGroupsLoaded(document.hamlet.language, groups)

        return {[document.hamlet.language]: document.hamlet.translations}
    }

    const cachedCoreMessages = localStorage.getItem(
        translationStorageKey(locale, CORE_TRANSLATION_GROUP),
    )
    if (!cachedCoreMessages) {
        return {}
    }

    try {
        markGroupsLoaded(locale, [CORE_TRANSLATION_GROUP])
        return {[locale]: JSON.parse(cachedCoreMessages)}
    } catch (error) {
        localStorage.removeItem(translationStorageKey(locale, CORE_TRANSLATION_GROUP))
        return {}
    }
}

export const i18n = new VueI18n({
    locale,
    fallbackLocale: 'en',
    messages: loadLocaleMessages(),
})

const mergeMessages = (language, messages, groups) => {
    i18n.mergeLocaleMessage(language, messages || {})
    markGroupsLoaded(language, groups)
    if (groups.includes(CORE_TRANSLATION_GROUP)) {
        localStorage.setItem(
            translationStorageKey(language, CORE_TRANSLATION_GROUP),
            JSON.stringify(messages || {}),
        )
    }
}

let bootstrapTranslationPromise = Promise.resolve()
if (document.hasOwnProperty('hamlet') && document.hamlet.tPromise) {
    const bootstrapLanguage = document.hamlet.language || locale
    const initialGroups = bootstrapGroups(document.hamlet.translationGroups)

    bootstrapTranslationPromise = document.hamlet.tPromise.then(result => {
        const isStructuredResult = result && Object.prototype.hasOwnProperty.call(result, 'loaded')
        const loaded = isStructuredResult ? result.loaded : true
        const messages = isStructuredResult ? result.messages : result

        if (loaded) {
            mergeMessages(bootstrapLanguage, messages, initialGroups)
        }
    })
}

const setI18nLanguage = language => {
    i18n.locale = language
    localStorage.setItem('locale', language)
    return language
}

const requestTranslationGroup = (language, group) => i18nApi
    .getTranslations(language, [group])
    .then(response => {
        mergeMessages(language, response.data.messages || {}, [group])
        return language
    })

const loadTranslationGroup = (language, group) => {
    if (groupsForLanguage(language).has(group)) {
        return Promise.resolve(language)
    }

    const promiseKey = `${language}:${group}`
    if (translationGroupPromises[promiseKey]) {
        return translationGroupPromises[promiseKey]
    }

    if (
        group === CORE_TRANSLATION_GROUP
        && document.hasOwnProperty('hamlet')
        && document.hamlet.language === language
    ) {
        translationGroupPromises[promiseKey] = bootstrapTranslationPromise
            .then(() => (
                groupsForLanguage(language).has(group)
                    ? language
                    : requestTranslationGroup(language, group)
            ))
    } else {
        translationGroupPromises[promiseKey] = requestTranslationGroup(language, group)
    }

    return translationGroupPromises[promiseKey]
        .then(result => {
            delete translationGroupPromises[promiseKey]
            return result
        })
        .catch(error => {
            delete translationGroupPromises[promiseKey]
            throw error
        })
}

export const loadTranslationGroups = (groups, language = i18n.locale) => {
    const normalizedGroups = normalizeGroups([CORE_TRANSLATION_GROUP, ...normalizeGroups(groups)])
    if (language === i18n.locale) {
        normalizedGroups.forEach(group => activeTranslationGroups.add(group))
    }

    const fallbackLanguage = typeof i18n.fallbackLocale === 'string'
        ? i18n.fallbackLocale
        : null
    const languages = translationLoadingConfig.loadFallbackTranslations
        && fallbackLanguage
        && fallbackLanguage !== language
        ? [fallbackLanguage, language]
        : [language]

    return Promise.all(languages.flatMap(targetLanguage => (
        normalizedGroups.map(group => loadTranslationGroup(targetLanguage, group))
    )))
        .then(() => language)
}

export function loadCurrentLanguageTranslations() {
    const groups = Array.from(activeTranslationGroups)

    return i18nApi.getTranslations(i18n.locale, groups).then(response => {
        mergeMessages(i18n.locale, response.data.messages || {}, groups)
        return i18n.locale
    })
}

export function updateTranslation(key, translations) {
    const translation = translations.find(item => item.language === i18n.locale)

    if (translation) {
        i18n.mergeLocaleMessage(i18n.locale, {[key]: translation.translation})
    }
}

export function loadLanguageAsync(language) {
    const groups = Array.from(activeTranslationGroups)

    return loadTranslationGroups(groups, language)
        .then(() => setI18nLanguage(language))
}

export {
    APPLICATION_TRANSLATION_GROUP,
    CORE_TRANSLATION_GROUP,
    DOCUMENT_REVIEW_TRANSLATION_GROUP,
    PRODUCT_COMPLIANCE_TRANSLATION_GROUP,
    RECEIVED_REQUEST_REPLY_TRANSLATION_GROUP,
    SUPPLIER_SELECTION_TRANSLATION_GROUP,
}
